import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import './Login.css';

const ResetTpin: React.FC = () => {
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    const mobileClean = mobile.replace(/\D/g, '').slice(-10);
    if (mobileClean.length !== 10) {
      setMessage({ type: 'error', text: 'Please enter a valid 10-digit mobile number.' });
      return;
    }
    if (!password.trim()) {
      setMessage({ type: 'error', text: 'Please enter your password.' });
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.requestTpin({ mobile: mobileClean, password: password.trim() });
      if (res.data?.success) {
        setMessage({ type: 'success', text: res.data.message || 'A new TPIN has been sent to your registered mobile number. It is valid for 15 days.' });
        setPassword('');
      } else {
        setMessage({ type: 'error', text: res.data?.message || 'Failed to send TPIN. Please try again.' });
      }
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.message || err?.message || 'Request failed. Please try again or contact support.'
      });
    } finally {
      setLoading(false);
    }
  };

  const inputBase = 'w-full py-3 px-4 pl-12 border-2 border-violet-500/60 rounded-xl bg-violet-600/70 text-white text-base placeholder-violet-200/80 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/40 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed';
  const labelBase = 'block text-sm font-bold text-white uppercase tracking-wider mb-2';

  return (
    <div className="min-h-screen min-w-full flex items-center justify-center p-5 relative overflow-hidden">
      <div
        className="fixed z-0"
        style={{ left: 0, top: 0, width: '100vw', height: '100vh', minWidth: '100%', minHeight: '100%', overflow: 'hidden', backgroundColor: '#0f172a' }}
      >
        <video autoPlay loop muted playsInline className="absolute" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)', minWidth: '100%', minHeight: '100%', width: '100%', height: '100%', objectFit: 'cover' }}>
          <source src="/login-bg.mp4" type="video/mp4" />
        </video>
      </div>
      <div className="fixed pointer-events-none z-[1] bg-slate-900/60" style={{ left: 0, top: 0, width: '100vw', height: '100vh' }} aria-hidden />
      <div className="fixed pointer-events-none z-[1] bg-[linear-gradient(rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:50px_50px]" style={{ left: 0, top: 0, width: '100vw', height: '100vh' }} aria-hidden />
      <div className="w-full max-w-md relative z-10 min-h-0">
        <div className="rounded-2xl border-2 border-violet-500/50 bg-violet-900/30 backdrop-blur-xl shadow-2xl shadow-violet-900/30 p-8 md:p-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img src={`${process.env.PUBLIC_URL || ''}/logo.png`} alt="YuvanAPI Logo" className="w-44 h-auto sm:w-52 object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-white uppercase tracking-wider m-0 mb-2">Reset TPIN</h1>
            <p className="text-sm text-violet-300/90 m-0">Enter your mobile and password to receive a new TPIN on your registered number</p>
            <div className="w-28 h-0.5 bg-gradient-to-r from-transparent via-violet-400 to-transparent mx-auto mt-4 rounded" />
          </div>

          {message && (
            <div className={`flex items-start gap-3 p-4 rounded-xl border-2 mb-5 ${message.type === 'success' ? 'border-emerald-500/50 bg-emerald-500/20 text-emerald-200' : 'border-red-500/50 bg-red-500/20 text-red-200'} text-sm whitespace-pre-line`}>
              <span className="flex-1">{message.text}</span>
              <button type="button" onClick={() => setMessage(null)} className="flex-shrink-0 p-1 rounded hover:opacity-80" aria-label="Dismiss">✕</button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6">
            <div className="mb-5 relative">
              <label htmlFor="tpin-mobile" className={labelBase}>Mobile Number</label>
              <div className="relative rounded-xl border-2 border-violet-500/60 bg-violet-600/70">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 16.92v3.02a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3.02a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <input id="tpin-mobile" type="tel" autoComplete="off" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile number" maxLength={10} disabled={loading} className="login-input-mobile w-full py-3 px-4 pl-12 rounded-xl bg-transparent border-0 text-white text-base placeholder-violet-200/80 focus:ring-2 focus:ring-violet-400/40 focus:outline-none disabled:opacity-60" />
              </div>
            </div>
            <div className="mb-5 relative">
              <label htmlFor="tpin-password" className={labelBase}>Password</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                <input id="tpin-password" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your account password" disabled={loading} className={inputBase} />
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full py-3.5 px-5 rounded-xl bg-violet-500/50 border-2 border-violet-400 text-white font-bold text-sm uppercase tracking-wider cursor-pointer hover:bg-violet-500/70 hover:border-violet-300 disabled:opacity-50 flex items-center justify-center gap-2">
              {loading && <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {loading ? 'Sending...' : 'Send new TPIN'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-violet-300 hover:text-violet-200 text-sm underline underline-offset-2">Back to Login</Link>
          </div>
          <div className="mt-8 pt-6 border-t border-violet-500/30 text-center">
            <p className="text-sm text-violet-300/80 m-0">Built by <span className="font-bold text-white">Algobasket</span></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetTpin;
